import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost, apiPut, apiDelete, formatLKR, formatPct } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Plus, Pencil, Trash2, Star, Briefcase, Crown, ArrowUp, ArrowDown,
  MessageSquare, ArrowUpDown, ChevronsUpDown, Check, FolderPlus, List, MoreVertical, FolderEdit, FolderX, Move,
} from "lucide-react";
import { toast } from "sonner";
import { useWatchlist } from "@/lib/watchlist";
import { useAuth } from "@/lib/auth";
import UsWatchlistPanel from "@/components/watchlist/UsWatchlistPanel";

const LS_WATCHLIST_MARKET_KEY = "watchlist-active-market";

// ===========================================================================
// Constants
// ===========================================================================
const SIGNAL_RANK = { BUY: 0, WATCH: 1, HOLD: 2, SELL: 3 };

const SIGNAL_FILTERS = [
  { value: "all", label: "All signals" },
  { value: "BUY", label: "BUY" },
  { value: "WATCH", label: "WATCH" },
  { value: "HOLD", label: "HOLD" },
];

const SIGNAL_BADGE_CLASS = {
  BUY: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  WATCH: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  HOLD: "bg-muted text-muted-foreground border-border",
  SELL: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const COLUMNS = [
  { key: "symbol", label: "Symbol & Company", sortable: true, mobile: true },
  { key: "current_price", label: "Last", sortable: true, align: "right", mobile: true, width: "100px" },
  { key: "change_abs", label: "Chg", sortable: true, align: "right", mobile: false, width: "80px" },
  { key: "change_pct", label: "%", sortable: true, align: "right", mobile: true, width: "80px" },
  { key: "signal", label: "Signal", sortable: true, align: "center", mobile: true, width: "84px" },
  { key: "score", label: "Score", sortable: true, align: "right", mobile: false, width: "60px" },
  { key: "dividend_yield", label: "Yield", sortable: true, align: "right", mobile: true, width: "76px" },
  { key: "target_buy_price", label: "Target", sortable: true, align: "right", mobile: false, width: "100px" },
  { key: "actions", label: "", sortable: false, align: "right", mobile: false, width: "120px" },
];

// ===========================================================================
// Sorting helpers
// ===========================================================================
const compareValues = (a, b, dir) => {
  const mult = dir === "asc" ? 1 : -1;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "string") return a.localeCompare(b) * mult;
  return (a - b) * mult;
};

const sortItems = (items, key, dir) => {
  const arr = [...items];
  switch (key) {
    case "symbol":
      arr.sort((a, b) => compareValues(a.cse_symbol, b.cse_symbol, dir));
      break;
    case "signal":
      arr.sort((a, b) =>
        compareValues(SIGNAL_RANK[a.signal] ?? 99, SIGNAL_RANK[b.signal] ?? 99, dir)
      );
      break;
    case "target_buy_price":
      arr.sort((a, b) => compareValues(a.target_buy_price, b.target_buy_price, dir));
      break;
    default:
      arr.sort((a, b) => compareValues(a[key], b[key], dir));
  }
  return arr;
};

// ===========================================================================
// Page
// ===========================================================================
const WatchlistPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refresh: refreshGlobal, remove: removeGlobal } = useWatchlist();
  const { isAdmin, user } = useAuth();
  const showUsTab = !!(isAdmin || (user && user.us_stocks_enabled));
  const [marketTab, setMarketTab] = useState(() => {
    if (!showUsTab) return "CSE";
    return localStorage.getItem(LS_WATCHLIST_MARKET_KEY) || "CSE";
  });
  useEffect(() => {
    localStorage.setItem(LS_WATCHLIST_MARKET_KEY, marketTab);
  }, [marketTab]);

  const [data, setData] = useState({ items: [], tier: "basic", limit: 10, count: 0, watchlists: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [securities, setSecurities] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [moveAfterAdd, setMoveAfterAdd] = useState(null);
  const [movingItem, setMovingItem] = useState(null);

  // Medium #13 — active named watchlist
  const [activeListId, setActiveListId] = useState(null);
  const [listManagerOpen, setListManagerOpen] = useState(false);
  const [renamingList, setRenamingList] = useState(null);
  const [deletingList, setDeletingList] = useState(null);
  const [maxLists, setMaxLists] = useState(1);

  const [sectorFilter, setSectorFilter] = useState("all");
  const [signalFilter, setSignalFilter] = useState("all");
  const [sortKey, setSortKey] = useState("added_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = (listId = activeListId) => {
    setLoading(true);
    const wlUrl = listId ? `/watchlist?watchlist_id=${encodeURIComponent(listId)}` : "/watchlist";
    Promise.all([apiGet(wlUrl), apiGet("/securities"), apiGet("/companies"), apiGet("/watchlist/lists")])
      .then(([wl, secs, cos, ls]) => {
        setData({ ...wl, watchlists: ls.watchlists || wl.watchlists || [] });
        setMaxLists(ls.max_lists || 1);
        const sectorBy = Object.fromEntries(cos.map((c) => [c.company_code, c.sector || "Other"]));
        setSecurities(secs.map((s) => ({ ...s, sector: sectorBy[s.company_code] || "Other" })));
        // If the active list was deleted server-side, reset to default
        const allIds = (ls.watchlists || []).map((w) => w.watchlist_id);
        if (listId && !allIds.includes(listId)) {
          setActiveListId(null);
        }
      })
      .catch((e) => setError(e.message || "Failed to load watchlist"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(activeListId); /* eslint-disable-next-line */ }, [activeListId]);

  // Handle ?just_purchased=SYMBOL — pop the post-move dialog
  useEffect(() => {
    const sym = searchParams.get("just_purchased");
    if (!sym) return;
    const item = (data.items || []).find((i) => i.cse_symbol === sym);
    if (item) setMoveAfterAdd(item);
    const next = new URLSearchParams(searchParams);
    next.delete("just_purchased");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.items]);

  const sectors = useMemo(() => {
    const s = new Set((data.items || []).map((i) => i.sector).filter(Boolean));
    return Array.from(s);
  }, [data.items]);

  const filtered = useMemo(() => {
    let arr = data.items || [];
    if (sectorFilter !== "all") arr = arr.filter((i) => i.sector === sectorFilter);
    if (signalFilter !== "all") arr = arr.filter((i) => (i.signal || "").toUpperCase() === signalFilter);
    if (sortKey === "added_at") {
      arr = [...arr].sort((a, b) => {
        const ta = new Date(a.added_at).getTime();
        const tb = new Date(b.added_at).getTime();
        return sortDir === "asc" ? ta - tb : tb - ta;
      });
    } else {
      arr = sortItems(arr, sortKey, sortDir);
    }
    return arr;
  }, [data.items, sectorFilter, signalFilter, sortKey, sortDir]);

  const onSortClick = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(col.key);
      setSortDir(col.key === "current_price" || col.key === "score" || col.key === "dividend_yield" ? "desc" : "asc");
    }
  };

  if (showUsTab && marketTab === "US") {
    // Pure US view — skip CSE data fetch entirely.
    return (
      <TooltipProvider delayDuration={250}>
        <div className="flex items-center justify-start mb-4" data-testid="watchlist-market-tabs-strip">
          <div className="inline-flex items-center rounded-lg border bg-muted/40 p-1">
            <button
              type="button"
              className="px-3 py-1.5 h-8 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground"
              onClick={() => setMarketTab("CSE")}
              data-testid="watchlist-tab-cse"
            >
              <span className="mr-1.5">🇱🇰</span> CSE Stocks
            </button>
            <button
              type="button"
              className="px-3 py-1.5 h-8 rounded-md text-sm font-medium transition-colors bg-background shadow-sm"
              data-testid="watchlist-tab-us"
            >
              <span className="mr-1.5">🇺🇸</span> US Stocks
            </button>
          </div>
        </div>
        <UsWatchlistPanel />
      </TooltipProvider>
    );
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  const tier = data.tier || "basic";
  const limit = data.limit;
  const count = data.count || 0;
  const watchlists = data.watchlists || [];
  const activeList = watchlists.find((w) => w.watchlist_id === activeListId) || watchlists.find((w) => w.is_default) || watchlists[0];
  const canCreateMore = watchlists.length < maxLists;

  const handleCreateList = async (name) => {
    try {
      const created = await apiPost("/watchlist/lists", { name });
      toast.success(`Created "${created.name}"`);
      setActiveListId(created.watchlist_id);
      setListManagerOpen(false);
      load(created.watchlist_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create list");
    }
  };

  const handleRenameList = async (watchlistId, name) => {
    try {
      await apiPut(`/watchlist/lists/${watchlistId}`, { name });
      toast.success("Renamed");
      setRenamingList(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename list");
    }
  };

  const handleDeleteList = async (watchlistId) => {
    try {
      const res = await apiDelete(`/watchlist/lists/${watchlistId}`);
      toast.success(`Deleted — ${res.moved_items || 0} item(s) moved to default`);
      setDeletingList(null);
      if (activeListId === watchlistId) setActiveListId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete list");
    }
  };

  const handleMoveItem = async (cseSymbol, targetId) => {
    try {
      await apiPost(`/watchlist/${cseSymbol}/move`, { watchlist_id: targetId });
      toast.success("Moved");
      setMovingItem(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  return (
    <TooltipProvider delayDuration={250}>
      {showUsTab ? (
        <div className="flex items-center justify-start mb-4" data-testid="watchlist-market-tabs-strip">
          <div className="inline-flex items-center rounded-lg border bg-muted/40 p-1">
            <button
              type="button"
              className={`px-3 py-1.5 h-8 rounded-md text-sm font-medium transition-colors ${marketTab === "CSE" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setMarketTab("CSE")}
              data-testid="watchlist-tab-cse"
            >
              <span className="mr-1.5">🇱🇰</span> CSE Stocks
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 h-8 rounded-md text-sm font-medium transition-colors ${marketTab === "US" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setMarketTab("US")}
              data-testid="watchlist-tab-us"
            >
              <span className="mr-1.5">🇺🇸</span> US Stocks
            </button>
          </div>
        </div>
      ) : null}

      {showUsTab && marketTab === "US" ? (
        <UsWatchlistPanel />
      ) : (
        <div className="space-y-4" data-testid="watchlist-page">
        {/* Single-row header bar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 mr-auto">
            <h2 className="font-display text-lg font-semibold whitespace-nowrap">My Watchlists</h2>
            {/* Medium #13 — Active list selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-9"
                  data-testid="watchlist-list-selector"
                >
                  <List className="h-3.5 w-3.5" />
                  <span className="max-w-[160px] truncate">{activeList?.name || "My Watchlist"}</span>
                  <span className="text-[10px] text-muted-foreground tabular">
                    {activeList?.count ?? 0}
                  </span>
                  <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Your Watchlists ({watchlists.length}/{maxLists})
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={activeList?.watchlist_id || ""}
                  onValueChange={(v) => setActiveListId(v)}
                >
                  {watchlists.map((w) => (
                    <DropdownMenuRadioItem
                      key={w.watchlist_id}
                      value={w.watchlist_id}
                      className="pr-2"
                      data-testid={`watchlist-list-option-${w.watchlist_id}`}
                    >
                      <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                        <span className="truncate">
                          {w.name}
                          {w.is_default && <span className="ml-1 text-[10px] text-muted-foreground">· default</span>}
                        </span>
                        <span className="text-[10px] text-muted-foreground tabular">{w.count}</span>
                      </div>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setListManagerOpen(true)}
                  disabled={!canCreateMore}
                  data-testid="watchlist-create-list-trigger"
                >
                  <FolderPlus className="h-3.5 w-3.5 mr-2" />
                  {canCreateMore ? "Create new list…" : `Limit reached (${maxLists})`}
                </DropdownMenuItem>
                {activeList && !activeList.is_default && (
                  <>
                    <DropdownMenuItem onClick={() => setRenamingList(activeList)} data-testid="watchlist-rename-list-trigger">
                      <FolderEdit className="h-3.5 w-3.5 mr-2" />
                      Rename "{activeList.name}"
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDeletingList(activeList)}
                      className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                      data-testid="watchlist-delete-list-trigger"
                    >
                      <FolderX className="h-3.5 w-3.5 mr-2" />
                      Delete "{activeList.name}"
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <span
              className="text-xs text-muted-foreground tabular"
              data-testid="watchlist-count"
            >
              {count} {count === 1 ? "stock" : "stocks"}
            </span>
            <TierPill tier={tier} count={count} limit={limit} />
          </div>
          <Select value={signalFilter} onValueChange={setSignalFilter}>
            <SelectTrigger
              className="w-[140px] h-9 text-xs"
              data-testid="watchlist-signal-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIGNAL_FILTERS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger
              className="w-[150px] h-9 text-xs"
              data-testid="watchlist-sector-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sectors</SelectItem>
              {sectors.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={`${sortKey}:${sortDir}`}
            onValueChange={(v) => {
              const [k, d] = v.split(":");
              setSortKey(k);
              setSortDir(d);
            }}
          >
            <SelectTrigger
              className="w-[170px] h-9 text-xs"
              data-testid="watchlist-sort"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="added_at:desc">Date Added</SelectItem>
              <SelectItem value="score:desc">Score (high → low)</SelectItem>
              <SelectItem value="dividend_yield:desc">Yield (high → low)</SelectItem>
              <SelectItem value="signal:asc">Signal (BUY first)</SelectItem>
              <SelectItem value="change_pct:desc">% Change (high → low)</SelectItem>
              <SelectItem value="symbol:asc">Symbol (A→Z)</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2 h-9" data-testid="watchlist-add-button">
                <Plus className="h-4 w-4" /> Add Stock
              </Button>
            </DialogTrigger>
            <AddDialog
              securities={securities}
              alreadyWatched={new Set((data.items || []).map((i) => i.cse_symbol))}
              targetWatchlist={activeList}
              onCreated={() => {
                setCreateOpen(false);
                load();
                refreshGlobal();
              }}
              onCancel={() => setCreateOpen(false)}
            />
          </Dialog>
        </div>

        {/* Empty state */}
        {(data.items || []).length === 0 ? (
          <EmptyState onAdd={() => setCreateOpen(true)} />
        ) : filtered.length === 0 ? (
          <div
            className="rounded-lg border bg-card py-10 text-center text-sm text-muted-foreground"
            data-testid="watchlist-empty-filters"
          >
            No watchlist items match the selected filters.
          </div>
        ) : (
          <div
            className="rounded-lg border bg-card overflow-hidden"
            data-testid="watchlist-table-wrapper"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="watchlist-table">
                <thead className="bg-card border-b sticky top-0 z-[1]">
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        className={`px-3 py-2.5 text-[10px] uppercase tracking-wide text-muted-foreground font-medium ${
                          col.align === "right"
                            ? "text-right"
                            : col.align === "center"
                              ? "text-center"
                              : "text-left"
                        } ${col.mobile ? "" : "hidden md:table-cell"} ${
                          col.sortable ? "cursor-pointer select-none hover:text-foreground" : ""
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                        onClick={() => onSortClick(col)}
                        data-testid={`watchlist-th-${col.key}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {col.sortable && sortKey === col.key && (
                            sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                          )}
                          {col.sortable && sortKey !== col.key && (
                            <ArrowUpDown className="h-3 w-3 opacity-30" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <Row
                      key={it.cse_symbol}
                      item={it}
                      watchlists={watchlists}
                      onClick={() =>
                        navigate(`/stocks/${it.company_code}`, {
                          state: { from: { pathname: "/watchlist", label: "Watchlist" } },
                        })
                      }
                      onEdit={() => setEditing(it)}
                      onDelete={() => setDeleting(it)}
                      onMoveToPortfolio={() => navigate(`/portfolio?add=${it.cse_symbol}`)}
                      onMoveList={(targetId) => handleMoveItem(it.cse_symbol, targetId)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <EditDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            refreshGlobal();
          }}
        />

        <DeleteDialog
          item={deleting}
          onCancel={() => setDeleting(null)}
          onConfirmed={async () => {
            try {
              await removeGlobal(deleting.cse_symbol);
              toast.success(`Removed ${deleting.cse_symbol} from watchlist`);
            } catch {
              toast.error("Remove failed");
            }
            setDeleting(null);
            load();
          }}
        />

        <PostMoveDialog
          item={moveAfterAdd}
          onKeep={() => setMoveAfterAdd(null)}
          onRemoved={async () => {
            try {
              await removeGlobal(moveAfterAdd.cse_symbol);
              toast.success("Removed from watchlist");
            } catch {
              /* noop */
            }
            setMoveAfterAdd(null);
            load();
          }}
        />

        {/* Medium #13 — Named watchlist management dialogs */}
        <CreateListDialog
          open={listManagerOpen}
          onClose={() => setListManagerOpen(false)}
          onCreate={handleCreateList}
        />
        <RenameListDialog
          list={renamingList}
          onClose={() => setRenamingList(null)}
          onRename={handleRenameList}
        />
        <DeleteListDialog
          list={deletingList}
          onCancel={() => setDeletingList(null)}
          onConfirm={handleDeleteList}
        />
        </div>
      )}
    </TooltipProvider>
  );
};

// ===========================================================================
// Row component (~48px)
// ===========================================================================
const Row = ({ item, watchlists = [], onClick, onEdit, onDelete, onMoveToPortfolio, onMoveList }) => {
  const targetReached = item.target_reached === true;
  const hasTarget = item.target_buy_price != null;
  const change = item.change_abs;
  const changePct = item.change_pct;
  const changeColor = (v) =>
    v == null
      ? "text-muted-foreground"
      : v > 0
        ? "text-[hsl(var(--success))]"
        : v < 0
          ? "text-[hsl(var(--destructive))]"
          : "text-muted-foreground";
  const signal = (item.signal || "").toUpperCase();

  return (
    <tr
      onClick={onClick}
      className={`group border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors duration-100 ${
        targetReached ? "border-l-4 border-l-[hsl(var(--success))]" : "border-l-4 border-l-transparent"
      }`}
      data-testid={`watchlist-row-${item.cse_symbol}`}
    >
      {/* 1. Symbol + Company */}
      <td className="px-3 py-2.5 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span
                className="font-mono text-xs font-semibold text-foreground"
                data-testid={`watchlist-symbol-${item.cse_symbol}`}
              >
                {item.cse_symbol}
              </span>
              <ShareBadge type={item.share_type} />
            </div>
            <div className="text-[11px] text-muted-foreground truncate max-w-[280px]">
              {item.company_name}
            </div>
          </div>
          {item.notes && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="ml-1 inline-flex"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`watchlist-notes-icon-${item.cse_symbol}`}
                >
                  <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs text-xs">
                {item.notes}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </td>
      {/* 2. Last Price */}
      <td className="px-3 py-2.5 text-right tabular font-semibold align-middle">
        {item.current_price != null ? formatLKR(item.current_price) : "—"}
      </td>
      {/* 3. Change */}
      <td
        className={`px-3 py-2.5 text-right tabular hidden md:table-cell align-middle ${changeColor(change)}`}
      >
        {change == null ? "—" : `${change > 0 ? "+" : ""}${change.toFixed(2)}`}
      </td>
      {/* 4. % */}
      <td className={`px-3 py-2.5 text-right tabular align-middle ${changeColor(changePct)}`}>
        {changePct == null ? "—" : `${changePct > 0 ? "+" : ""}${changePct.toFixed(2)}%`}
      </td>
      {/* 5. Signal */}
      <td className="px-3 py-2.5 text-center align-middle">
        {signal ? (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
              SIGNAL_BADGE_CLASS[signal] || SIGNAL_BADGE_CLASS.HOLD
            }`}
            data-testid={`watchlist-signal-${item.cse_symbol}`}
          >
            {signal}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      {/* 6. Score */}
      <td className="px-3 py-2.5 text-right tabular text-muted-foreground hidden md:table-cell align-middle">
        {item.score != null ? item.score : "—"}
      </td>
      {/* 7. Yield */}
      <td className="px-3 py-2.5 text-right tabular align-middle">
        {item.dividend_yield != null ? formatPct(item.dividend_yield) : "—"}
      </td>
      {/* 8. Target */}
      <td className="px-3 py-2.5 text-right tabular hidden md:table-cell align-middle">
        {hasTarget ? (
          <span className="inline-flex items-center gap-1.5 justify-end">
            {targetReached && (
              <span
                aria-label="Price at or below target"
                className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--success))]"
                data-testid={`watchlist-target-dot-${item.cse_symbol}`}
              />
            )}
            <span className={targetReached ? "text-[hsl(var(--success))] font-semibold" : ""}>
              {formatLKR(item.target_buy_price)}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      {/* 9. Actions */}
      <td className="px-3 py-2.5 text-right hidden md:table-cell align-middle">
        <div
          className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onEdit}
                aria-label="Edit"
                data-testid={`watchlist-edit-${item.cse_symbol}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onMoveToPortfolio}
                aria-label="Add to Portfolio"
                data-testid={`watchlist-move-${item.cse_symbol}`}
              >
                <Briefcase className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add to Portfolio</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:text-[hsl(var(--destructive))]"
                onClick={onDelete}
                aria-label="Remove"
                data-testid={`watchlist-delete-${item.cse_symbol}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remove from watchlist</TooltipContent>
          </Tooltip>
          {watchlists.length > 1 && (
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label="More"
                      data-testid={`watchlist-more-${item.cse_symbol}`}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Move to another list</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Move to list
                </DropdownMenuLabel>
                {watchlists
                  .filter((w) => w.watchlist_id !== item.watchlist_id)
                  .map((w) => (
                    <DropdownMenuItem
                      key={w.watchlist_id}
                      onClick={() => onMoveList?.(w.watchlist_id)}
                      data-testid={`watchlist-move-to-${w.watchlist_id}-${item.cse_symbol}`}
                    >
                      <Move className="h-3.5 w-3.5 mr-2" />
                      {w.name}
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </td>
    </tr>
  );
};

// ===========================================================================
// Subcomponents
// ===========================================================================
const ShareBadge = ({ type }) => {
  const isVoting = type === "voting";
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide ${
        isVoting
          ? "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border border-[hsl(var(--info))]/30"
          : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30"
      }`}
      title={isVoting ? "Voting share" : "Non-voting share"}
    >
      {isVoting ? "V" : "NV"}
    </span>
  );
};

const TierPill = ({ tier, count, limit }) => {
  const pct = limit ? Math.min(100, Math.round((count / limit) * 100)) : 0;
  return (
    <div className="hidden sm:flex items-center gap-2 rounded-full border bg-background px-3 py-1 text-[11px]">
      {tier === "premium" ? (
        <Crown className="h-3 w-3 text-[hsl(45_95%_55%)]" />
      ) : (
        <Star className="h-3 w-3 text-muted-foreground" />
      )}
      <span className="font-medium capitalize">{tier}</span>
      <span className="text-muted-foreground tabular">
        {count} / {limit ?? "∞"}
      </span>
      {limit != null && (
        <div className="ml-1 h-1 w-10 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full ${
              pct >= 100 ? "bg-[hsl(var(--destructive))]" : "bg-[hsl(var(--primary))]"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ onAdd }) => (
  <div className="rounded-lg border bg-card py-16 text-center" data-testid="watchlist-empty">
    <Star className="h-8 w-8 mx-auto mb-3 text-muted-foreground/60" />
    <div className="font-display text-base font-medium">Your watchlist is empty</div>
    <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
      Add stocks you want to monitor before buying.
    </p>
    <Button size="sm" className="mt-4 gap-2" onClick={onAdd} data-testid="watchlist-empty-add-button">
      <Plus className="h-4 w-4" /> Add Stock
    </Button>
  </div>
);

// ===========================================================================
// Dialogs
// ===========================================================================
const AddDialog = ({ securities, alreadyWatched, targetWatchlist, onCreated, onCancel }) => {
  const { add } = useWatchlist();
  const [cseSymbol, setCseSymbol] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const sectorGroups = useMemo(() => {
    const groups = {};
    for (const s of securities) {
      const sector = s.sector || "Other";
      if (!groups[sector]) groups[sector] = [];
      groups[sector].push(s);
    }
    return Object.entries(groups)
      .map(([sector, items]) => ({
        sector,
        items: items.sort((a, b) => a.cse_symbol.localeCompare(b.cse_symbol)),
      }))
      .sort((a, b) => a.sector.localeCompare(b.sector));
  }, [securities]);

  const selected = securities.find((s) => s.cse_symbol === cseSymbol);

  const submit = async () => {
    if (!cseSymbol) {
      toast.error("Choose a stock");
      return;
    }
    setBusy(true);
    try {
      await add(cseSymbol, {
        target_buy_price: target ? Number(target) : undefined,
        notes: notes || undefined,
        watchlist_id: targetWatchlist?.watchlist_id,
      });
      toast.success(`Added to "${targetWatchlist?.name || 'My Watchlist'}"`);
      onCreated();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Failed to add to watchlist";
      toast.error(msg, { duration: status === 403 ? 6000 : 4000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent
      className="sm:max-w-[480px]"
      data-testid="watchlist-add-dialog"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>Add stock to watchlist</DialogTitle>
        <DialogDescription>
          Track a stock without buying it. Set a target buy price to be alerted when
          it drops to your zone.
          {targetWatchlist && (
            <span className="block mt-1.5 text-xs">
              Target list: <strong>{targetWatchlist.name}</strong>
            </span>
          )}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <Label htmlFor="wl-symbol">Stock</Label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="w-full justify-between font-normal"
                id="wl-symbol"
                data-testid="watchlist-symbol-select"
              >
                {selected ? (
                  <span className="truncate">
                    <span className="font-mono text-foreground">{selected.cse_symbol}</span>
                    <span className="text-muted-foreground"> — {selected.company_name || selected.display_name}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Choose a security…</span>
                )}
                <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 w-[var(--radix-popover-trigger-width)]"
              align="start"
              data-testid="watchlist-symbol-popover"
            >
              <Command>
                <CommandInput placeholder="Search by ticker, name or sector…" />
                <CommandList className="max-h-72">
                  <CommandEmpty>No security found.</CommandEmpty>
                  {sectorGroups.map((g) => (
                    <CommandGroup key={g.sector} heading={`${g.sector} (${g.items.length})`}>
                      {g.items.map((s) => {
                        const taken = alreadyWatched.has(s.cse_symbol);
                        const value = `${s.cse_symbol} ${s.company_name || ""} ${g.sector}`;
                        return (
                          <CommandItem
                            key={s.cse_symbol}
                            value={value}
                            disabled={taken}
                            onSelect={() => {
                              if (!taken) {
                                setCseSymbol(s.cse_symbol);
                                setPickerOpen(false);
                              }
                            }}
                            data-testid={`watchlist-symbol-option-${s.cse_symbol}`}
                          >
                            <Check
                              className={`mr-2 h-3.5 w-3.5 ${cseSymbol === s.cse_symbol ? "opacity-100" : "opacity-0"}`}
                            />
                            <span className="font-mono text-xs w-24 shrink-0">{s.cse_symbol}</span>
                            <span className="text-sm truncate">{s.company_name || s.display_name}</span>
                            {taken && (
                              <span className="ml-auto text-[10px] text-muted-foreground">already watched</span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wl-target">Target buy price (optional)</Label>
          <Input
            id="wl-target"
            type="number"
            step="0.01"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="e.g. 70.00"
            data-testid="watchlist-add-target"
          />
          <p className="text-[11px] text-muted-foreground">
            Auto-creates a price-below alert when set.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wl-notes">Notes (optional)</Label>
          <Textarea
            id="wl-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Waiting for Q3 results"
            data-testid="watchlist-add-notes"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="watchlist-add-submit">
          {busy ? "Adding…" : "Add to Watchlist"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const EditDialog = ({ item, onClose, onSaved }) => {
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setTarget(item.target_buy_price != null ? String(item.target_buy_price) : "");
      setNotes(item.notes || "");
    }
  }, [item]);

  if (!item) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await apiPut(`/watchlist/${item.cse_symbol}`, {
        target_buy_price: target === "" ? null : Number(target),
        notes,
      });
      toast.success("Watchlist item updated");
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Update failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-[440px]"
        data-testid="watchlist-edit-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Edit watchlist item</DialogTitle>
          <DialogDescription>
            {item.company_name} — {item.cse_symbol}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ew-target">Target buy price</Label>
            <Input
              id="ew-target"
              type="number"
              step="0.01"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="watchlist-edit-target"
            />
            <p className="text-[11px] text-muted-foreground">
              Leaving blank deactivates the linked price-below alert.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ew-notes">Notes</Label>
            <Textarea
              id="ew-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="watchlist-edit-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="watchlist-edit-submit">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteDialog = ({ item, onCancel, onConfirmed }) => {
  if (!item) return null;
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="watchlist-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {item.cse_symbol} from watchlist?</AlertDialogTitle>
          <AlertDialogDescription>
            The auto-created price-below alert (if any) will be deactivated. You can add the
            stock back any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirmed}
            data-testid="watchlist-delete-confirm"
            className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white"
          >
            Yes, remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const PostMoveDialog = ({ item, onKeep, onRemoved }) => {
  if (!item) return null;
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => !o && onKeep()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {item.cse_symbol} from watchlist?</AlertDialogTitle>
          <AlertDialogDescription>
            You've added this stock to your portfolio. Would you like to remove it from
            your watchlist now that you've purchased it?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onKeep}>Keep in Watchlist</AlertDialogCancel>
          <AlertDialogAction onClick={onRemoved}>Yes, remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default WatchlistPage;

// ===========================================================================
// Medium #13 — Named watchlist management dialogs
// ===========================================================================
const CreateListDialog = ({ open, onClose, onCreate }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) setName("");
  }, [open]);
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try { await onCreate(trimmed); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px]" data-testid="watchlist-create-list-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Create new watchlist</DialogTitle>
          <DialogDescription>Organise stocks into separate themed lists — e.g. "Dividend Plays", "Growth Watch".</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="wl-new-name">List name</Label>
          <Input
            id="wl-new-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dividend Plays"
            maxLength={60}
            data-testid="watchlist-create-list-name"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !name.trim()} data-testid="watchlist-create-list-submit">
            {busy ? "Creating…" : "Create list"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RenameListDialog = ({ list, onClose, onRename }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (list) setName(list.name); }, [list]);
  if (!list) return null;
  const submit = async () => {
    const t = name.trim();
    if (!t || t === list.name) { onClose(); return; }
    setBusy(true);
    try { await onRename(list.watchlist_id, t); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px]" data-testid="watchlist-rename-list-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Rename watchlist</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="wl-rename-name">New name</Label>
          <Input
            id="wl-rename-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            data-testid="watchlist-rename-list-name"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !name.trim()} data-testid="watchlist-rename-list-submit">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteListDialog = ({ list, onCancel, onConfirm }) => {
  if (!list) return null;
  return (
    <AlertDialog open={!!list} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="watchlist-delete-list-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete "{list.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            All {list.count || 0} stock{list.count === 1 ? "" : "s"} in this list will be moved back to your default watchlist.
            The list itself will be permanently deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(list.watchlist_id)}
            data-testid="watchlist-delete-list-confirm"
            className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white"
          >
            Yes, delete list
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
